import React, { useState, useEffect } from 'react';
import { EventModal } from './EventModal';
import { VideoModal } from './VideoModal';
import { ClusteredEventsTable } from './ClusteredEventsTable';
import { TimePeriodSelector } from './TimePeriodSelector';
import { api } from '../services/api';

export const Events = ({ events, loading, onEventsUpdate, onEventSelect, selectedEventId }) => {
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [timePeriod, setTimePeriod] = useState('ALL');
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [comment, setComment] = useState('');
  const [downloadingEvents, setDownloadingEvents] = useState(new Set());
  const [panelStatuses, setPanelStatuses] = useState({});
  const [isPanelStatusLoading, setIsPanelStatusLoading] = useState(false);

  // Filter events based on time period
  useEffect(() => {
    if (!events || events.length === 0) {
      setFilteredEvents([]);
      return;
    }

    const now = new Date();
    let filterDate = new Date();

    switch (timePeriod) {
      case 'DAY':
        filterDate.setDate(now.getDate() - 1);
        break;
      case 'MONTH':
        filterDate.setMonth(now.getMonth() - 1);
        break;
      case '3MONTHS':
        filterDate.setMonth(now.getMonth() - 3);
        break;
      case 'ALL':
      default:
        // No date filtering for "All Time"
        setFilteredEvents(events);
        return;
    }

    const filtered = events.filter(event => {
      const eventDate = new Date(event.timestamp);
      return eventDate >= filterDate;
    });

    setFilteredEvents(filtered);
  }, [events, timePeriod]);

  // Fetch panel statuses when events change
  useEffect(() => {
    const fetchPanelStatuses = async () => {
      if (!events || events.length === 0) return;
      
      // Collect unique location codes from events
      const locationCodes = new Set();
      events.forEach(event => {
        if (event.locationCode) {
          locationCodes.add(event.locationCode);
        }
      });
      
      if (locationCodes.size === 0) return;
      
      setIsPanelStatusLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://13.61.79.6:5005/api';
        const response = await fetch(`${apiUrl}/panel-status`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch panel statuses');
        }
        
        // Create a map of locationCode -> status
        const statusMap = {};
        data.panels.forEach(panel => {
          statusMap[panel.locationCode] = panel.status;
        });
        
        setPanelStatuses(statusMap);
      } catch (error) {
        console.error('Error fetching panel statuses:', error);
      } finally {
        setIsPanelStatusLoading(false);
      }
    };
    
    fetchPanelStatuses();
  }, [events]);

  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
  };

  const checkVideoExists = async (url) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Error checking video:', error);
      return false;
    }
  };

  const handleViewVideo = async (event, type = 'preview') => {
    console.log('Opening video for event:', event);
    
    // Use the message field which contains the complete event string
    const message = event.message;
    if (!message) {
      console.error('No event message found');
      return;
    }

    try {
      // Replace spaces and commas with underscores
      const safeFilename = message.replace(/[ ,]/g, '_');
      
      // Construct the appropriate URL based on type
      const baseUrl = process.env.REACT_APP_API_VIDEOS_URL; 
      let url;
      
      if (type === 'preview') {
        url = `${baseUrl}/preview/${encodeURIComponent(safeFilename)}_preview.mp4`;
      } else {
        // For full video, remove '_preview' if it exists and add '_full'
        const baseFilename = safeFilename.replace('_preview', '');
        url = `${baseUrl}/full/${encodeURIComponent(baseFilename)}_full.mp4`;
      }
      
      // Check if video exists before showing modal
      const exists = await checkVideoExists(url);
      if (!exists) {
        alert('Video is not ready');
        return;
      }
      
      console.log('Video URL:', url);
      setVideoUrl(url);
      setShowVideoModal(true);
    } catch (error) {
      console.error('Error opening video:', error);
      alert('Error accessing video: ' + error.message);
    }
  };

  const handleCloseVideo = () => {
    setShowVideoModal(false);
    setVideoUrl('');
  };

  const handleAcknowledge = (event, e) => {
    e.preventDefault();
    onEventSelect(event._id);
    setSelectedEvent(event);
    setShowStatusModal(true);
  };

  // Check if a panel is online
  const isPanelOnline = (locationCode) => {
    if (!locationCode || isPanelStatusLoading) return true; // Default to true if we don't have location info
    return panelStatuses[locationCode] !== 'OFFLINE';
  };

  const handleDownloadVideo = async (event) => {
    // Check if panel is offline
    if (event.locationCode && !isPanelOnline(event.locationCode)) {
      alert('Cannot download video: Panel is offline');
      return;
    }

    try {
      // Set loading state for this specific event
      setDownloadingEvents(prev => new Set([...prev, event._id]));
      
      console.log('Starting download for event:', event._id);
      
      // Check if we have the proper event ID to pass
      // Your backend is looking for the eventId field, not _id
      const eventId = event.eventId || event._id;
      
      // Use the environment variable for the API URL
      const apiUrl = process.env.REACT_APP_API_URL || 'http://13.61.79.6:5005/api';
      
      // Call the download API endpoint with the full URL
      const response = await fetch(`${apiUrl}/download-video?eventId=${encodeURIComponent(eventId)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        const textContent = await response.clone().text();
        console.error('Response content:', textContent.substring(0, 200));
        throw new Error('Server returned invalid JSON response. Check server logs.');
      }
      
      if (!response.ok) {
        throw new Error(data.message || `Request failed with status: ${response.status}`);
      }
      
      console.log('Download started successfully:', data);
      
      // Update the event status locally to show immediate feedback
      const updatedEvents = events.map(e => {
        if (e._id === event._id && e.videoDetails) {
          return {
            ...e,
            videoDetails: {
              ...e.videoDetails,
              status: 'RECEIVING'
            }
          };
        }
        return e;
      });
      
      // If there's an onEventsUpdate handler, call it with the updated events
      if (onEventsUpdate) {
        onEventsUpdate(updatedEvents);
      }
      
      // Show success message to user
      alert('Video download started successfully');
      
    } catch (error) {
      console.error('Error downloading video:', error);
      alert(`Error downloading video: ${error.message}`);
    } finally {
      // Clear loading state for this event
      setDownloadingEvents(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(event._id);
        return newSet;
      });
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!comment.trim()) {
      alert('Please provide a comment');
      return;
    }

    try {
      setIsLoading(true);
      await api.acknowledgeEvent(selectedEvent._id);
      await api.updateEventStatus(selectedEvent._id, status, comment);
      
      setShowStatusModal(false);
      setComment('');
      setSelectedEvent(null);
      onEventSelect(null);
      
      if (onEventsUpdate) {
        onEventsUpdate();
      }
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowStatusModal(false);
    setComment('');
    setSelectedEvent(null);
    onEventSelect(null);
  };

  return (
    <div className="pt-20 pb-20">
      <div className="fixed top-16 left-0 right-0 bg-white z-40">
        <div className="px-6 pb-2">
          <div className="py-4 bg-white border-b flex justify-between items-center">
            <TimePeriodSelector 
              selectedPeriod={timePeriod} 
              onPeriodChange={handleTimePeriodChange} 
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Events</option>
              <option value="NEEDS_REVIEW">Needs Review</option>
              <option value="CONFIRMED">Confirmed Alarms</option>
              <option value="FALSE">False Alarms</option>
            </select>
          </div>
        </div>
      </div>

      <div className="px-6 pt-8">
        <ClusteredEventsTable
          events={filteredEvents}
          filterStatus={filterStatus}
          onAcknowledge={handleAcknowledge}
          isLoading={isLoading || downloadingEvents.size > 0 || isPanelStatusLoading}
          onViewVideo={handleViewVideo}
          onDownloadVideo={handleDownloadVideo}
          panelStatuses={panelStatuses}
          isPanelOnline={isPanelOnline}
        />
      </div>
      
      <EventModal
        show={showStatusModal}
        onClose={handleModalClose}
        onStatusUpdate={handleStatusUpdate}
        comment={comment}
        setComment={setComment}
        isLoading={isLoading}
      />

      <VideoModal
        show={showVideoModal}
        onClose={handleCloseVideo}
        videoUrl={videoUrl}
      />
    </div>
  );
};